import { Snackbar as MuiSnackbar, Alert, AlertColor } from '@mui/material';

import { createContext, Dispatch, SetStateAction, useState, useContext, useEffect } from 'react';

import { isMobile } from '../../lib/util/checkPlatform';

type AlertSnackbarState = {
  severity?: AlertColor;
  message: string;
  duration?: number;
  vertical?: 'bottom' | 'top';
  horizontal?: 'center' | 'left' | 'right';
};

type AlertSnackbarProviderProps = {
  children: JSX.Element;
};

const initialValues: AlertSnackbarState = {
  duration: 0,
  message: '',
  vertical: 'bottom',
  horizontal: 'center',
};

export const AlertSnackbarContext = createContext<[AlertSnackbarState, Dispatch<SetStateAction<AlertSnackbarState>>]>([
  initialValues,
  () => {},
]);

export const AlertSnackbarProvider = ({ children }: AlertSnackbarProviderProps) => {
  const state = useState<AlertSnackbarState>(initialValues);

  return <AlertSnackbarContext.Provider value={state}>{children}</AlertSnackbarContext.Provider>;
};

export const AlertSnackbar = () => {
  const [state] = useContext(AlertSnackbarContext);
  const { duration = 6000, severity, message = '', vertical = 'top', horizontal = 'right' } = state;

  const [open, setOpen] = useState(false);

  const handleClose = () => setOpen(false);

  const mobilePosition = {
    vertical: 'top' as const,
    horizontal: 'center' as const,
  };

  const desktopPosition = {
    vertical,
    horizontal,
  };

  useEffect(() => {
    if (!state?.message) {
      return;
    }
    setOpen(true);
  }, [state]);

  return (
    <>
      <MuiSnackbar
        open={open}
        autoHideDuration={duration}
        anchorOrigin={isMobile() ? mobilePosition : desktopPosition}
        onClose={handleClose}
        sx={{
          ...(isMobile() && {
            top: '16px !important',
            left: '16px !important',
            right: '16px !important',
            width: 'calc(100% - 32px)',
            maxWidth: 'calc(100% - 32px)',
          }),
        }}
      >
        <Alert
          onClose={handleClose}
          severity={severity}
          sx={{
            width: '100%',
            maxWidth: '100%',
            wordBreak: 'break-word',
            whiteSpace: 'pre-wrap',
            overflowWrap: 'break-word',
            ...(isMobile() && {
              fontSize: '0.875rem',
            }),
          }}
        >
          {message}
        </Alert>
      </MuiSnackbar>
    </>
  );
};
